import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, Copy, ClipboardList, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ArahkanButton } from "@/components/network/ArahkanButton";
import { OdpPortsPanel } from "@/components/network/OdpPortsPanel";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { OpticalPathList } from "@/components/network/OpticalPath";
import { SplitterRelations } from "@/components/network/SplitterRelations";
import { KodeHistory } from "@/components/network/KodeHistory";
import { StatusPill, NoCoordBadge, PortBar, InServerBadge, inServerText } from "@/components/network/StatusPill";
import { ServerInfo } from "@/components/network/ServerInfo";
import { OdpSplitters } from "@/components/network/OdpSplitters";
import { canManageWilayah, canEditInfra } from "@/lib/network";

function Row({ k, children }) {
  return <div className="grid grid-cols-[110px_1fr] gap-2 border-b border-[#F3F4F6] py-1.5 text-xs"><span className="text-[#6B7280]">{k}</span><span>{children}</span></div>;
}

export function InfraDetailPanel({ sel, me, wilayahName, lokasiName, onClose, onSelect, onChanged, onDeleted, opticalPath }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("info");
  const nav = useNavigate();
  useEffect(() => {
    setD(null); setTab("info");
    api.get(`/network/${sel.kind}/${sel.id}`).then((r) => setD(r.data)).catch(() => toast.error("Gagal memuat detail"));
  }, [sel]);
  const copy = () => { navigator.clipboard?.writeText(`${d.latitude}, ${d.longitude}`); toast.success("Koordinat disalin"); };
  const label = sel.kind === "odc" ? "ODC" : "ODP";
  return (
    <aside className="flex h-full flex-col bg-white" data-testid="infra-detail-panel">
      <div className="flex items-center justify-between border-b p-3">
        <b data-testid="infra-detail-title">Detail {label}</b>
        <Button size="icon" variant="ghost" onClick={onClose} data-testid="infra-detail-close"><X className="h-4 w-4" /></Button>
      </div>
      {!d ? <p className="p-3 text-xs">Memuat…</p> : (
        <div className="flex-1 space-y-3 overflow-y-auto p-3">
          <div>
            <div className="font-display text-xl font-bold" data-testid="infra-detail-kode">{d.kode}</div>
            <div className="text-xs text-[#6B7280]">{d.nama} · {wilayahName(d.wilayah_id)}</div>
          </div>
          {sel.kind === "odp" && (
            <div className="flex gap-1">
              {["info", "port"].map((t) => (
                <Button key={t} size="sm" variant={tab === t ? "default" : "outline"} onClick={() => setTab(t)} data-testid={`infra-tab-${t}`}>
                  {t === "info" ? "Informasi" : `Port (${d.port_terpakai}/${d.kapasitas_port})`}
                </Button>
              ))}
            </div>
          )}
          {tab === "info" && (
            <div>
              {sel.kind === "odp" && <Row k="ODC Induk">
                {d.odc ? <button className="font-semibold text-[#0055FF] hover:underline" onClick={() => onSelect({ kind: "odc", id: d.odc_id })} data-testid="infra-detail-parent">
                  {d.odc.kode} ({d.odc.nama})</button> : <span data-testid="infra-detail-parent-none">-</span>}
                {d.in_server && d.odc?.in_server?.id === d.in_server.id && <p className="mt-1 text-[11px] text-[#334155]" data-testid="infra-detail-same-container">
                  Kedua ujung berada di {d.in_server.label} {d.in_server.kode} yang sama — garis relasi tidak digambar di peta.</p>}
                {d.splitter_feed && <p className="mt-1 text-[11px] text-[#334155]" data-testid="infra-detail-splitter-feed">
                  Disuplai Splitter <b className="font-mono-tab">{d.splitter_feed.kode}</b> ({d.splitter_feed.rasio}) output {d.splitter_feed.output_port}; {d.odc ? "ODC induk mengikuti jalur splitter." : "jalur splitter tidak sampai ke ODC, ODC induk kosong."}</p>}</Row>}
              <Row k="Lokasi">{lokasiName(d.lokasi_id)}</Row>
              <Row k="Alamat">{d.alamat || "-"}</Row>
              <Row k="Koordinat">{d.in_server ? <InServerBadge s={d.in_server} testid="infra-detail-inserver" /> : d.has_coords
                ? <span className="inline-flex items-center gap-1 font-mono-tab">{d.latitude}, {d.longitude}<button onClick={copy} title="Salin"><Copy className="h-3 w-3" /></button></span>
                : <NoCoordBadge testid="infra-detail-nocoord" />}</Row>
              {sel.kind === "odc" ? <Row k="Kapasitas Splitter">{d.kapasitas_splitter > 0 ? d.kapasitas_splitter : "-"}</Row> : (
                <>
                  <Row k="Kapasitas">{d.kapasitas_port} port</Row>
                  <Row k="Terpakai"><PortBar used={d.port_terpakai} cap={d.kapasitas_port} testid="infra-detail-bar" /></Row>
                  <Row k="Tersedia">{d.port_kosong} port</Row>
                  <Row k="Tanggal Pasang">{d.tanggal_pasang || "-"}</Row>
                </>
              )}
              <Row k="Status"><StatusPill status={d.status} /></Row>
              <Row k="Catatan">{d.catatan || "-"}</Row>
              {sel.kind === "odp" && opticalPath !== undefined && <div className="mt-3"><OpticalPathList path={opticalPath} onSelect={onSelect} /></div>}
              <div className="mt-3"><SplitterRelations kind={sel.kind} id={d.id} kode={d.kode} onSelect={onSelect} /></div>
              <div className="mt-3"><ServerInfo kind={sel.kind} id={d.id} /></div>
              {sel.kind === "odp" && <div className="mt-3"><OdpSplitters odpId={d.id} me={me} onSelect={onSelect} onChanged={onChanged} /></div>}
              <div className="mt-3"><KodeHistory kind={sel.kind} id={d.id} kode={d.kode} /></div>
              {sel.kind === "odc" && (
                <div className="mt-3" data-testid="infra-detail-odps">
                  <div className="mb-1 text-xs font-bold">ODP di bawah ODC ini ({d.odps.length})</div>
                  {d.odps.map((o) => (
                    <button key={o.id} data-testid={`infra-detail-child-${o.kode}`} onClick={() => onSelect({ kind: "odp", id: o.id })} className="flex w-full items-center justify-between rounded px-1 py-1 text-xs hover:bg-[#F3F4F6]">
                      <span><b>{o.kode}</b> {o.in_server ? `· ${inServerText(o.in_server)}${d.in_server?.id === o.in_server.id ? ` (${o.in_server.label} sama, garis tidak digambar)` : ""}` : !o.has_coords && "· tanpa koordinat"}</span><PortBar used={o.port_terpakai} cap={o.kapasitas_port} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {tab === "port" && (
            <>
              <OdpPortsPanel odpId={d.id} me={{ ...me, can_manage: false, is_admin: false, wilayah_ids: [] }} compact onChanged={onChanged} />
              {canManageWilayah(me, d.wilayah_id) && <p className="text-[10px] text-[#6B7280]">Penempatan pelanggan dikelola di Master ODP (tombol Kelola).</p>}
            </>
          )}
          <div className="flex flex-wrap gap-2 border-t pt-3">
            {d.has_coords && <ArahkanButton lat={d.latitude} lng={d.longitude} testid="infra-nav-btn" />}
            {d.has_coords && <Button size="sm" variant="outline" asChild data-testid="infra-open-maps">
              <a href={`https://www.google.com/maps/search/?api=1&query=${d.latitude},${d.longitude}`} target="_blank" rel="noreferrer">Lihat di Maps</a></Button>}
            <Button size="sm" variant="outline" onClick={() => nav("/report")} data-testid="infra-create-temuan"><ClipboardList className="mr-1 h-3.5 w-3.5" />Buat Temuan</Button>
            {canEditInfra(me) && (
              <Button size="sm" variant="outline" onClick={() => nav(sel.kind === "odc" ? "/network/odc" : `/network/odp?odp_id=${d.id}`)} data-testid="infra-edit-link">
                <Pencil className="mr-1 h-3.5 w-3.5" />Kelola</Button>
            )}
            <DeleteInfraButton kind={sel.kind} item={d} me={me} onDeleted={onDeleted} />
          </div>
          {!me?.can_manage && <p className="text-center text-[10px] text-[#6B7280]">Edit hanya untuk Admin dan Koordinator</p>}
        </div>
      )}
    </aside>
  );
}
